import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';
import { useAuth } from '@/app/AuthContext.jsx';
import { useDocumentTitle, useLedgerQuery } from '@/hooks';
import { PageHead, Tabs, Skel } from '@/ui';
import { getChannelIntakes } from '@/lib/recepthor-api';
import { qk } from '@/lib/queryKeys';
import WhatsAppIntakeCard from './whatsapp/WhatsAppIntakeCard';
import WhatsAppIntakeDetail from './whatsapp/WhatsAppIntakeDetail';
import AceptarWhatsAppModal from './whatsapp/AceptarWhatsAppModal';
import { channelTermsLine } from './whatsapp/acceptCopy';
import { useIntakeDecisions } from './whatsapp/useIntakeDecisions';
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery';

import './PlataformaPagos.css';
import './PlataformaWhatsApp.css';

const QUEUE_KEY = qk.channelIntakeQueue.key;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const EMPTY = {
  pending:
    'No hay pedidos WhatsApp. Van a aparecer cuando Kapso entregue un hilo.',
  accepted: 'Ningún pedido aceptado todavía.',
  rejected: 'Ningún pedido rechazado.',
};

export default function PlataformaWhatsApp() {
  useDocumentTitle('WhatsApp · Plataforma');
  const { setBreadcrumb, setPills } = useOutletContext();
  const organizationId = useAuth()?.organizationId ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedId = searchParams.get('id');
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const panelId = useId();
  const listRef = useRef(null);

  const [tab, setTab] = useState('pending');
  // The Slack alert links here with ?id=. Scroll to that card once per id, the
  // first time it renders, so a refetch or "Cargar más" does not jump again.
  // A click marks its own id first: that card is already on screen.
  const scrolledIdRef = useRef(null);
  const scrollToSelected = useCallback(
    (node) => {
      if (!node || !selectedId || scrolledIdRef.current === selectedId) return;
      scrolledIdRef.current = selectedId;
      if (typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ block: 'center' });
      }
    },
    [selectedId],
  );
  const {
    busyIds,
    preflightOpen,
    accepting,
    acceptDialog,
    onAccept,
    onReject,
    closeAccept,
    submitAccept,
  } = useIntakeDecisions({ organizationId });

  const pending = useLedgerQuery({
    resourceKey: QUEUE_KEY,
    fetcher: getChannelIntakes,
    params: { filter: 'pending' },
    pageSize: 50,
    surfaceErrors: true,
  });
  const accepted = useLedgerQuery({
    resourceKey: QUEUE_KEY,
    fetcher: getChannelIntakes,
    params: { filter: 'accepted' },
    pageSize: 50,
    surfaceErrors: true,
  });
  const rejected = useLedgerQuery({
    resourceKey: QUEUE_KEY,
    fetcher: getChannelIntakes,
    params: { filter: 'rejected' },
    pageSize: 50,
    surfaceErrors: true,
  });
  const queues = [pending, accepted, rejected];

  useEffect(() => {
    setBreadcrumb(['WhatsApp']);
    setPills([]);
    return () => {
      setBreadcrumb([]);
      setPills([]);
    };
  }, [setBreadcrumb, setPills]);

  const lane =
    tab === 'accepted' ? accepted : tab === 'rejected' ? rejected : pending;

  // Picking from the list with nothing open pushes, so Back returns to the
  // list. Moving from one pedido to another replaces and keeps the same mark.
  const openIntake = useCallback(
    (row) => {
      scrolledIdRef.current = row.id;
      if (row.id === selectedId) return;
      const next = new URLSearchParams(searchParams);
      next.set('id', row.id);
      if (selectedId) {
        setSearchParams(next, { replace: true, state: location.state });
      } else {
        setSearchParams(next, { state: { fromList: true } });
      }
    },
    [location.state, searchParams, selectedId, setSearchParams],
  );

  // Opened from the list: going back is the same as the browser's Back. Opened
  // from a link (Slack, another tab): drop the id without adding an entry.
  // One close per location: a double click on × or a held Esc must not go
  // back twice before the first one lands.
  const closingRef = useRef(false);
  useEffect(() => {
    closingRef.current = false;
  }, [location.key]);
  const closeIntake = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    if (location.state?.fromList) {
      navigate(-1);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next, { replace: true });
  }, [location.state, navigate, searchParams, setSearchParams]);

  // Focus goes back to the card that was open. Closing can also be the
  // browser's Back, so this follows the id instead of the close click. It
  // tries once, right after the close: on the phone the list mounts in that
  // same commit. A card that is not in the active tab (a link to another tab,
  // a row not loaded yet) leaves focus on the queue instead.
  const prevSelectedRef = useRef(selectedId);
  useEffect(() => {
    const prev = prevSelectedRef.current;
    prevSelectedRef.current = selectedId;
    if (selectedId || !prev) return;
    const list = listRef.current;
    if (!list) return;
    const button = Array.from(list.querySelectorAll('[data-intake-open]')).find(
      (node) => node.getAttribute('data-intake-open') === prev,
    );
    (button ?? list).focus();
  }, [selectedId]);

  // The selected pedido comes from any of the three loaded queues, not only
  // the active tab, so it stays open after it is accepted or rejected. While
  // a refetch moves it between queues, the last row seen holds the panel.
  const loadedRow = selectedId
    ? (queues
        .map((q) => q.items.find((row) => row.id === selectedId))
        .find(Boolean) ?? null)
    : null;
  const lastRowRef = useRef(null);
  useEffect(() => {
    if (loadedRow) lastRowRef.current = loadedRow;
  }, [loadedRow]);
  const refreshing = queues.some((q) => q.isFetching);
  const selectedRow =
    loadedRow ??
    (selectedId && refreshing && lastRowRef.current?.id === selectedId
      ? lastRowRef.current
      : null);
  // A queue that failed to load may hold the pedido, so that is an error, not
  // a pedido that is missing.
  const panelKind = !selectedId
    ? 'empty'
    : selectedRow
      ? 'found'
      : queues.some((q) => q.isLoading)
        ? 'loading'
        : queues.some((q) => q.isError)
          ? 'error'
          : 'missing';
  const retryQueues = () => {
    queues.forEach((q) => {
      if (q.isError) q.refetch();
    });
  };
  // An id that is not a uuid never shows up in a later page either.
  const canLoadMore =
    UUID_RE.test(selectedId ?? '') && queues.some((q) => q.hasNextPage);
  const loadingMore = queues.some((q) => q.isFetchingNextPage);
  const loadMore = () => {
    queues.forEach((q) => {
      if (q.hasNextPage && !q.isFetchingNextPage) q.fetchNextPage();
    });
  };

  const renderCard = (row) => (
    <WhatsAppIntakeCard
      key={row.id}
      row={row}
      selected={Boolean(selectedId) && selectedId === row.id}
      scrollRef={scrollToSelected}
      controls={desktop ? panelId : undefined}
      onOpen={openIntake}
      busy={busyIds.has(row.id)}
      preflightOpen={preflightOpen}
      accepting={accepting}
      onAccept={onAccept}
      onReject={onReject}
    />
  );

  const errorState = (q) => (
    <div className="plp-empty is-err">
      No se pudo cargar WhatsApp.{' '}
      <button type="button" className="plp-morebtn" onClick={() => q.refetch()}>
        Reintentar
      </button>
    </div>
  );

  const laneBody = (q, emptyMsg) => {
    if (q.isError) return errorState(q);
    if (q.isLoading)
      return (
        <div className="plp-cards" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="plp-card" style={{ pointerEvents: 'none' }}>
              <div className="plp-card__head">
                <div className="plp-card__lead" style={{ gap: 12 }}>
                  <Skel w={60} />
                  <Skel w={80} />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    if (!q.items.length) return <div className="plp-empty">{emptyMsg}</div>;
    return (
      <>
        <div className="plp-cards">{q.items.map((row) => renderCard(row))}</div>
        {q.hasNextPage ? (
          <div className="plp-more">
            <button
              type="button"
              className="plp-morebtn"
              disabled={q.isFetchingNextPage}
              onClick={() => q.fetchNextPage()}
            >
              {q.isFetchingNextPage
                ? 'Cargando…'
                : `Cargar más (${q.items.length} de ${q.total})`}
            </button>
          </div>
        ) : null}
      </>
    );
  };

  const tabItems = [
    {
      key: 'pending',
      label: 'Pendientes',
      count: pending.total,
      active: tab === 'pending',
    },
    {
      key: 'accepted',
      label: 'Aceptados',
      count: accepted.total,
      active: tab === 'accepted',
    },
    {
      key: 'rejected',
      label: 'Rechazados',
      count: rejected.total,
      active: tab === 'rejected',
    },
  ];

  // On the phone an open pedido takes the whole screen.
  const showQueue = desktop || !selectedId;
  const showPanel = desktop || Boolean(selectedId);

  return (
    <div className="page reveal">
      <PageHead
        kicker="Plataforma"
        title="WhatsApp"
        sub={
          <>
            Pedidos WhatsApp · {pending.total} pendiente(s) · {accepted.total}{' '}
            aceptado(s) · {rejected.total} rechazado(s)
          </>
        }
      />

      {showQueue ? (
        <div className="plp-tabs">
          <Tabs items={tabItems} onChange={(_i, item) => setTab(item.key)} />
        </div>
      ) : null}

      <div className="pli-workspace">
        {showQueue ? (
          <section
            ref={listRef}
            className="plp-franja pli-queue"
            aria-label="Cola de pedidos WhatsApp"
            tabIndex={-1}
          >
            {laneBody(lane, EMPTY[tab])}
          </section>
        ) : null}
        {showPanel ? (
          <WhatsAppIntakeDetail
            panelId={panelId}
            intakeId={selectedId}
            kind={panelKind}
            row={selectedRow}
            mobile={!desktop}
            onClose={closeIntake}
            busy={selectedRow ? busyIds.has(selectedRow.id) : false}
            preflightOpen={preflightOpen}
            accepting={accepting}
            onAccept={onAccept}
            onReject={onReject}
            canLoadMore={canLoadMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
            onRetry={retryQueues}
          />
        ) : null}
      </div>
      {acceptDialog && (
        <AceptarWhatsAppModal
          dialog={acceptDialog}
          termsLine={channelTermsLine(acceptDialog.terms)}
          busy={accepting}
          onClose={closeAccept}
          onSubmit={submitAccept}
          onRetry={() => submitAccept(acceptDialog.retryBody)}
        />
      )}
    </div>
  );
}
